import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Package,
  FileText,
  FolderOpen,
  MessageSquare,
  Users,
  Shield,
  FileCheck,
  User as UserIcon,
  BarChart3,
  ScrollText,
  Wallet,
} from 'lucide-react';

export const Sidebar = () => {
  const location = useLocation();
  const { permissions, user } = useAuth();

  const isAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'OPERADOR' ||
    user?.role === 'SUPERVISOR' ||
    permissions?.can_view_admin;

  const isSuperAdmin = user?.role === 'SUPERADMIN' || permissions?.can_view_superadmin;

  const userNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Productos', href: '/products', icon: Package },
    { name: 'Cotizaciones', href: '/quotes', icon: FileText, permission: 'can_create_quotes' },
    { name: 'Polizas', href: '/policies', icon: Shield, permission: 'can_issue_policies' },
    { name: 'Mis Pagos', href: '/payments', icon: Wallet },
    { name: 'Documentos', href: '/documents', icon: FolderOpen, permission: 'can_upload_documents' },
    { name: 'Solicitudes', href: '/requests', icon: MessageSquare, permission: 'can_create_requests' },
  ];

  const adminMainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  ];

  const adminNavigation = [
    { name: 'Usuarios', href: '/admin/users', icon: Users, permission: 'can_manage_users' },
    { name: 'Cotizaciones', href: '/admin/quotes', icon: FileText, permission: 'can_view_admin' },
    { name: 'Polizas', href: '/admin/policies', icon: Shield, permission: 'can_view_all_policies' },
    { name: 'Solicitudes', href: '/admin/requests', icon: MessageSquare, permission: 'can_manage_requests' },
    { name: 'Documentos', href: '/admin/documents', icon: FileCheck, permission: 'can_validate_documents' },
    { name: 'Auditoria', href: '/admin/audit', icon: ScrollText, permission: 'can_view_admin' },
    { name: 'ACSEL', href: '/admin/acsel-audit', icon: ScrollText, permission: 'can_view_admin' },
  ];

  const superAdminNavigation = [
    { name: 'Analytics', href: '/superadmin', icon: BarChart3 },
    { name: 'Productos', href: '/superadmin/products', icon: Package },
    { name: 'Auditoria', href: '/superadmin/audit', icon: ScrollText },
  ];

  const mainNavigation = isAdmin && !isSuperAdmin ? adminMainNavigation : isAdmin && isSuperAdmin ? [] : userNavigation;
  const filteredNavigation = mainNavigation.filter(
    (item) => !item.permission || (permissions && permissions[item.permission])
  );
  const filteredAdminNav = adminNavigation.filter(
    (item) => !item.permission || (permissions && permissions[item.permission])
  );
  const hasAdminAccess = isAdmin && filteredAdminNav.length > 0;

  const NavLink = ({ item }) => {
    const exactOnly = item.href === '/dashboard' || item.href === '/superadmin';
    const isActive = exactOnly
      ? location.pathname === item.href
      : location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    const Icon = item.icon;

    return (
      <Link
        to={item.href}
        data-testid={`nav-${item.href.replace(/\//g, '-').replace(/^-/, '')}`}
        className={cn(
          'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-primary text-primary-foreground shadow-md'
            : 'text-foreground/70 hover:bg-muted hover:text-foreground'
        )}
      >
        <Icon className={cn('w-[18px] h-[18px]', isActive && 'scale-110')} />
        <span>{item.name}</span>
      </Link>
    );
  };

  return (
    <aside
      className="flex flex-col border-r border-border bg-white"
      style={{ width: 'var(--sidebar-width)' }}
    >
      {/* Logo */}
      <div className="p-5 border-b border-border">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/isologo-azul.png"
            alt="Seguros Horizonte"
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="font-bold text-base text-primary leading-tight" style={{ fontFamily: "'Libre Baskerville', Georgia, serif" }}>
              Horizonte
            </h1>
            <p className="text-[11px] text-muted-foreground">a tu lado</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto hide-scrollbar">
        <div className="space-y-0.5">
          {filteredNavigation.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </div>

        {hasAdminAccess && (
          <>
            <div className="my-4 px-3">
              <div className="border-t border-border" />
            </div>
            <div className="px-3 mb-2">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                Administracion
              </p>
            </div>
            <div className="space-y-0.5">
              {filteredAdminNav.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>
          </>
        )}

        {isSuperAdmin && (
          <>
            <div className="my-4 px-3">
              <div className="border-t border-border" />
            </div>
            <div className="px-3 mb-2">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                Super Admin
              </p>
            </div>
            <div className="space-y-0.5">
              {superAdminNavigation.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>
          </>
        )}

        <div className="mt-4 pt-4 border-t border-border">
          <NavLink item={{ name: 'Perfil', href: '/profile', icon: UserIcon }} />
        </div>
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-border">
        <p className="text-[10px] text-center text-muted-foreground">v1.0.0</p>
      </div>
    </aside>
  );
};
